import { Dispatch, SetStateAction, useMemo } from 'react'

import { isWeekend } from 'date-fns'
import { Ellipsis, FileDown, Mail, Play, PlusCircle } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { LoadingDropdownMenuItem } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { ICGuidelines } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { useGuidelineDrawer } from '@/pages/funds/ic-guidelines-v2/components/GuidelineDrawerContext'
import { useFunds } from '@/react-query/queries/funds/funds'
import { useReportStream } from '@/react-query/use-report-stream'

export interface ReportControlsProps {
  reportStream: ReturnType<typeof useReportStream>
  params: ICGuidelines.GetICGuidelinesParams
  setParams: Dispatch<SetStateAction<ICGuidelines.GetICGuidelinesParams>>
}

export const ReportControls: React.FC<ReportControlsProps> = ({
  reportStream,
  params,
  setParams,
}) => {
  const { openCreate } = useGuidelineDrawer()
  const { data: funds, query: fundsQuery } = useFunds()

  // ... Only funds that are currently active
  const filteredFunds = useMemo(() => {
    return (funds ?? []).filter((fund) => {
      return fund.fund_name && fund.active
    })
  }, [funds])

  const fundOptions = useMemo(() => {
    return filteredFunds.map((fund) => ({
      label: fund.fund_name,
      value: fund.fund_name,
    }))
  }, [filteredFunds])

  return (
    <HeaderConfigBar sticky>
      <div className={'flex items-center gap-2'}>
        <SearchSelect
          size={'sm'}
          options={fundOptions}
          placeholder={'Select a fund...'}
          value={params.fund_name}
          loading={fundsQuery.isFetching}
          setValue={(val) => {
            setParams((prev) => {
              const foundFund = filteredFunds.find((f) => f.fund_name === val)
              return { ...prev, fund_name: foundFund?.fund_name! }
            })
          }}
        />
        <DatePickerWithControls
          selected={new Date(params.value_date)}
          todayButton={{
            enabled: true,
            text: 'T - 1',
            date: getTMinus1(),
          }}
          disabled={(date) => isWeekend(date) || date > getTMinus1()}
          size="sm"
          onSelect={(date) => {
            if (date) {
              setParams((prev) => ({ ...prev, value_date: toISO8601(date) }))
            }
          }}
        />

        <LoadingButton
          size="sm"
          variant="outline"
          icon={<Play />}
          onClick={() => reportStream.startAsync()}
          disabled={!params.fund_name || !params.value_date}
        >
          Run Report
        </LoadingButton>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Wrap in div to allow 'asChild' tooltip to work properly with nested button components */}
              <div>
                <DropdownMenuTrigger asChild>
                  <Button variant={'outline'} size="sm" disabled={false}>
                    <Ellipsis />
                    Download
                  </Button>
                </DropdownMenuTrigger>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side={'right'}
              className={cn('max-w-44 text-center', {
                ['hidden']: false,
              })}
            >
              You must receive data before you can download.
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align={'start'}>
            <DropdownMenuItemIndicator />
            <LoadingDropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
              }}
              onClick={async () => {
                // downloadExcelMutation.mutateAsync(params)
              }}
            >
              <FileDown />
              As Excel
            </LoadingDropdownMenuItem>
            <LoadingDropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
              }}
              onClick={async () => {
                // forwardEmailMutation.mutateAsync(params)
              }}
            >
              <Mail />
              By Email
            </LoadingDropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <CanAccess permissions={[PERMISSIONS.WRITE_IC_GUIDELINES]}>
          <Button size="sm" variant={'outline'} onClick={() => openCreate()}>
            Create Guideline <PlusCircle />
          </Button>
        </CanAccess>
      </div>
    </HeaderConfigBar>
  )
}
